import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  BookOpen, Heart, Star, Trophy, Play, ChevronRight, Clock, Award,
  Check, Cross, Music, Palette, Target, Flame, ArrowLeft, Volume2,
  Sparkles, X, Loader2, HelpCircle, Image as ImageIcon, Medal, Users, Puzzle, Swords
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import VoiceSelector, { VOICE_OPTIONS } from '@/components/VoiceSelector';
import { DailyChallengeCard } from '@/components/DailyChallengeCard';
import { speak as _speakTTS, stopSpeaking as _stopTTS } from '@/utils/speak';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChristianChildDashboard = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [child, setChild] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('home');
  
  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResults, setQuizResults] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Leaderboard state
  const [leaderboard, setLeaderboard] = useState(null);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

  // Word puzzles state
  const [puzzleType, setPuzzleType] = useState(null);
  const [puzzleSubject, setPuzzleSubject] = useState('bible');
  const [puzzle, setPuzzle] = useState(null);
  const [generatingPuzzle, setGeneratingPuzzle] = useState(false);
  const [puzzleResult, setPuzzleResult] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, [childId]);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      if (!token) {
        navigate('/christian/login');
        return;
      }
      const response = await axios.get(`${API_URL}/api/christian/child/${childId}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChild(response.data.child);
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      if (error.response?.status === 401) {
        navigate('/christian/login');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      setLoadingLeaderboard(true);
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/christian/worksheet/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(r.data);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'leaderboard') fetchLeaderboard();
  }, [activeTab]);

  // Word puzzle functions
  const generatePuzzle = async (type) => {
    setGeneratingPuzzle(true);
    setPuzzleResult(null);
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/word-puzzles/generate?puzzle_type=${type}&subject=${puzzleSubject}&child_id=${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPuzzle(r.data);
      setPuzzleType(type);
    } catch (err) {
      toast.error('Failed to generate puzzle');
    } finally {
      setGeneratingPuzzle(false);
    }
  };

  const submitPuzzle = async (answers) => {
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const r = await axios.post(`${API_URL}/api/word-puzzles/submit`, {
        child_id: childId, puzzle_type: puzzleType, answers, time_spent: 0,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPuzzleResult(r.data);
      toast.success(`+${r.data.xp_earned} XP!`);
      if (r.data.xp_earned > 0) {
        setChild(prev => ({ ...prev, total_xp: (prev?.total_xp || 0) + r.data.xp_earned }));
      }
    } catch (err) {
      toast.error('Failed to submit puzzle');
    }
  };

  // Start a new quiz
  const startQuiz = async () => {
    setQuizLoading(true);
    setQuizResults(null);
    setQuizAnswers({});
    setCurrentQuestionIndex(0);
    
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/christian/child/${childId}/quiz`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuizData(response.data);
      setShowQuiz(true);
    } catch (error) {
      toast.error('Failed to load quiz');
      console.error('Quiz error:', error);
    } finally {
      setQuizLoading(false);
    }
  };

  // Submit quiz answers
  const submitQuiz = async () => {
    setQuizLoading(true);
    
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const answers = Object.entries(quizAnswers).map(([question_id, answer]) => ({
        question_id,
        answer
      }));
      
      const response = await axios.post(
        `${API_URL}/api/christian/child/${childId}/quiz/submit`,
        { answers, quiz_type: 'mixed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setQuizResults(response.data);
      
      // Update local child XP
      if (response.data.xp_earned > 0) {
        setChild(prev => ({
          ...prev,
          total_xp: response.data.new_total_xp
        }));
      }
      
      // Show celebration for perfect score
      if (response.data.perfect_score) {
        toast.success('Perfect Score! You earned ' + response.data.xp_earned + ' XP!', {
          icon: '🌟'
        });
      } else if (response.data.xp_earned > 0) {
        toast.success('Great job! You earned ' + response.data.xp_earned + ' XP!');
      }
      
      // Show badge notification
      if (response.data.new_badge) {
        setTimeout(() => {
          toast.success('New Badge: ' + response.data.new_badge.badge_name + '!', {
            icon: '🏆'
          });
        }, 1500);
      }
    } catch (error) {
      toast.error('Failed to submit quiz');
      console.error('Submit error:', error);
    } finally {
      setQuizLoading(false);
    }
  };

  // Handle answer selection
  const handleAnswer = (questionId, answer) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  // Get current question
  const currentQuestion = quizData?.questions?.[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === (quizData?.questions?.length || 0) - 1;
  const allAnswered = quizData?.questions?.every(q => quizAnswers[q.id]);

  // AI-powered natural voice TTS
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('shimmer');
  const [speechSpeed, setSpeechSpeed] = useState(0.9);
  const audioRef = useRef(null);

  const speakText = async (text) => {
    if (!text) return;
    
    // Stop any current audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    setIsSpeaking(true);
    
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/christian/tts`,
        { text, voice: selectedVoice, speed: speechSpeed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const audioData = response.data.audio;
        const audioBlob = await fetch(`data:audio/mp3;base64,${audioData}`).then(r => r.blob());
        const audioUrl = URL.createObjectURL(audioBlob);
        
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        
        audio.onended = () => {
          setIsSpeaking(false);
          URL.revokeObjectURL(audioUrl);
        };
        
        audio.onerror = () => {
          setIsSpeaking(false);
          // Fallback to browser TTS
          _speakTTS(text, 0.9);
        };
        
        audio.play();
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsSpeaking(false);
      // Fallback to browser TTS
      _speakTTS(text, 0.9);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      seeds: 'from-green-400 to-emerald-500',
      roots: 'from-blue-400 to-indigo-500',
      warriors: 'from-purple-400 to-pink-500'
    };
    return colors[level] || colors.seeds;
  };

  const getLevelEmoji = (level) => {
    const emojis = { seeds: '🌱', roots: '🌿', warriors: '⚔️' };
    return emojis[level] || '🌱';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-purple-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Cross className="w-8 h-8 text-white" />
          </div>
          <p className="text-purple-300">Loading...</p>
        </div>
      </div>
    );
  }

  const weekData = dashboard?.current_lesson?.data;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-purple-950 pb-24">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-white/10 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/christian/parent')} className="text-purple-400">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getLevelColor(child?.level)} flex items-center justify-center text-2xl`}>
              {getLevelEmoji(child?.level)}
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">{child?.name}</h1>
              <p className="text-xs text-purple-400 capitalize">Level: {child?.level}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-amber-500/20 rounded-full px-3 py-1">
            <Star className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-bold text-amber-400">{child?.total_xp || 0} XP</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="p-4 space-y-4">
        {activeTab === 'home' && (
          <>
            {/* Today's Lesson Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-gradient-to-br from-indigo-900/50 to-purple-900/50 border-purple-500/30 overflow-hidden">
                <CardContent className="p-0">
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-indigo-200 text-xs mb-1">Week {dashboard?.current_lesson?.week} • {dashboard?.current_lesson?.quarter?.replace('_', ' ')?.toUpperCase()}</p>
                        <h2 className="text-xl font-bold text-white">{weekData?.topic}</h2>
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-white/20 hover:bg-white/30 text-white border-0"
                        onClick={() => navigate(`/christian/lesson/${childId}/${dashboard?.current_lesson?.week}`)}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Start
                      </Button>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-slate-300 mb-3">{weekData?.character_lesson}</p>
                    <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg p-3">
                      <BookOpen className="w-5 h-5 text-purple-400" />
                      <span className="text-sm text-purple-300">{weekData?.scripture}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Verse of the Week */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="bg-slate-800/50 border-amber-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-white flex items-center gap-2">
                    <Heart className="w-5 h-5 text-amber-400" />
                    Verse of the Week
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 rounded-xl p-4 border border-amber-500/20">
                    <p className="text-amber-300 text-sm mb-2">{dashboard?.verse_reference}</p>
                    <p className="text-white text-lg font-medium leading-relaxed">"{dashboard?.verse_of_week}"</p>
                    <div className="mt-3 flex flex-col gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/20 w-fit"
                        onClick={() => speakText(dashboard?.verse_of_week)}
                        disabled={isSpeaking}
                      >
                        {isSpeaking ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Volume2 className="w-4 h-4 mr-2" />
                        )}
                        {isSpeaking ? 'Reading...' : 'Listen'}
                      </Button>
                      <VoiceSelector
                        voice={selectedVoice}
                        onVoiceChange={setSelectedVoice}
                        speed={speechSpeed}
                        onSpeedChange={setSpeechSpeed}
                        compact={true}
                        theme="dark"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Quick Actions */}
            <div className="grid grid-cols-3 gap-3">
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                onClick={() => navigate(`/christian/lesson/${childId}/${dashboard?.current_lesson?.week || 1}`)}
                className="bg-gradient-to-br from-pink-500/20 to-purple-500/20 border border-pink-500/30 rounded-2xl p-4 text-left hover:scale-[1.02] transition-transform"
                data-testid="bible-games-btn"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center mb-2">
                  <Palette className="w-5 h-5 text-white" />
                </div>
                <p className="font-medium text-white text-sm">Activities</p>
                <p className="text-xs text-slate-400">Games</p>
              </motion.button>

              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.25 }}
                onClick={() => setActiveTab('memory')}
                className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-2xl p-4 text-left hover:scale-[1.02] transition-transform"
                data-testid="memory-verses-btn"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-2">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <p className="font-medium text-white text-sm">Verses</p>
                <p className="text-xs text-slate-400">{child?.scriptures_memorized || 0} done</p>
              </motion.button>

              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                onClick={() => navigate(`/christian/songs/${childId}`)}
                className="bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-2xl p-4 text-left hover:scale-[1.02] transition-transform"
                data-testid="worship-songs-btn"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-2">
                  <Music className="w-5 h-5 text-white" />
                </div>
                <p className="font-medium text-white text-sm">Songs</p>
                <p className="text-xs text-slate-400">Sing along</p>
              </motion.button>

              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.35 }}
                onClick={() => setActiveTab('badges')}
                className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl p-4 text-left hover:scale-[1.02] transition-transform"
                data-testid="my-badges-btn"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center mb-2">
                  <Award className="w-5 h-5 text-white" />
                </div>
                <p className="font-medium text-white text-sm">Badges</p>
                <p className="text-xs text-slate-400">{dashboard?.badges?.length || 0} earned</p>
              </motion.button>

              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                onClick={() => navigate(`/christian/gallery/${childId}`)}
                className="bg-gradient-to-br from-fuchsia-500/20 to-rose-500/20 border border-fuchsia-500/30 rounded-2xl p-4 text-left hover:scale-[1.02] transition-transform"
                data-testid="gallery-btn"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-fuchsia-500 to-rose-600 rounded-xl flex items-center justify-center mb-2">
                  <ImageIcon className="w-5 h-5 text-white" />
                </div>
                <p className="font-medium text-white text-sm">Gallery</p>
                <p className="text-xs text-slate-400">Art collection</p>
              </motion.button>

              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.45 }}
                onClick={() => navigate(`/multiplayer/${childId}`)}
                className="bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/30 rounded-2xl p-4 text-left hover:scale-[1.02] transition-transform"
                data-testid="multiplayer-btn"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center mb-2">
                  <Swords className="w-5 h-5 text-white" />
                </div>
                <p className="font-medium text-white text-sm">Multiplayer</p>
                <p className="text-xs text-slate-400">Play with friends</p>
              </motion.button>
            </div>

            {/* Daily Challenge */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
              <DailyChallengeCard childId={childId} theme="dark" />
            </motion.div>

            {/* Progress Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card className="bg-slate-800/50 border-slate-700/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-white flex items-center gap-2">
                    <Target className="w-5 h-5 text-emerald-400" />
                    My Progress
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-400">Lessons Completed</span>
                      <span className="text-emerald-400">{child?.lessons_completed || 0}/52</span>
                    </div>
                    <Progress value={((child?.lessons_completed || 0) / 52) * 100} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-400">Verses Memorized</span>
                      <span className="text-purple-400">{child?.scriptures_memorized || 0}</span>
                    </div>
                    <Progress value={Math.min((child?.scriptures_memorized || 0) / 50 * 100, 100)} className="h-2" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </>
        )}

        {activeTab === 'badges' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">My Badges</h2>
              <button onClick={() => setActiveTab('home')} className="text-purple-400 text-sm">
                Back
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Badge Progress */}
              {[
                { type: 'bronze', name: 'Bronze Badge', verses: 5, icon: '🥉', earned: (child?.scriptures_memorized || 0) >= 5 },
                { type: 'silver', name: 'Silver Badge', verses: 20, icon: '🥈', earned: (child?.scriptures_memorized || 0) >= 20 },
                { type: 'gold', name: 'Gold Badge', verses: 50, icon: '🥇', earned: (child?.scriptures_memorized || 0) >= 50 },
                { type: 'champion', name: 'Champion', verses: 100, icon: '🏆', earned: (child?.scriptures_memorized || 0) >= 100 }
              ].map((badge) => (
                <Card 
                  key={badge.type} 
                  className={`border ${badge.earned ? 'bg-gradient-to-br from-amber-900/30 to-orange-900/30 border-amber-500/50' : 'bg-slate-800/30 border-slate-700/50'}`}
                >
                  <CardContent className="p-4 text-center">
                    <div className={`text-4xl mb-2 ${badge.earned ? '' : 'grayscale opacity-50'}`}>
                      {badge.icon}
                    </div>
                    <p className="font-medium text-white text-sm">{badge.name}</p>
                    <p className="text-xs text-slate-400 mb-2">{badge.verses} verses</p>
                    {badge.earned ? (
                      <span className="text-xs text-emerald-400 bg-emerald-500/20 px-2 py-1 rounded-full">
                        <Check className="w-3 h-3 inline mr-1" />
                        Earned!
                      </span>
                    ) : (
                      <Progress 
                        value={Math.min(((child?.scriptures_memorized || 0) / badge.verses) * 100, 100)} 
                        className="h-1.5"
                      />
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Recent Badges */}
            {dashboard?.badges?.length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700/50">
                <CardHeader>
                  <CardTitle className="text-base text-white">Recently Earned</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {dashboard.badges.map((badge, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-slate-700/30 rounded-lg p-3">
                      <span className="text-2xl">{badge.badge_type === 'bronze' ? '🥉' : badge.badge_type === 'silver' ? '🥈' : badge.badge_type === 'gold' ? '🥇' : '🏆'}</span>
                      <div>
                        <p className="font-medium text-white text-sm">{badge.badge_name}</p>
                        <p className="text-xs text-slate-400">{badge.requirement} verses memorized</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'memory' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Memory Verses</h2>
              <button onClick={() => setActiveTab('home')} className="text-purple-400 text-sm">
                Back
              </button>
            </div>

            {/* Quiz Me Button */}
            <Card className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 border-purple-500/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-white">Scripture Quiz</p>
                      <p className="text-sm text-purple-300">Test your memory & earn XP!</p>
                    </div>
                  </div>
                  <Button
                    onClick={startQuiz}
                    disabled={quizLoading}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                    data-testid="start-quiz-btn"
                  >
                    {quizLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Quiz Me!
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Current Verse to Learn */}
            <Card className="bg-gradient-to-br from-emerald-900/50 to-teal-900/50 border-emerald-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-white flex items-center gap-2">
                  <Heart className="w-5 h-5 text-emerald-400" />
                  This Week's Verse
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-emerald-300 text-sm mb-2">{dashboard?.verse_reference}</p>
                <p className="text-white text-lg leading-relaxed">"{dashboard?.verse_of_week}"</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button 
                    size="sm" 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => speakText(dashboard?.verse_of_week)}
                    disabled={isSpeaking}
                  >
                    {isSpeaking ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Volume2 className="w-4 h-4 mr-2" />
                    )}
                    {isSpeaking ? 'Reading...' : 'Listen'}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="border-emerald-500/50 text-emerald-400"
                    onClick={() => {
                      toast.success('Great job practicing!');
                    }}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    I Know It!
                  </Button>
                </div>
                <div className="mt-3 pt-3 border-t border-emerald-500/20">
                  <VoiceSelector
                    voice={selectedVoice}
                    onVoiceChange={setSelectedVoice}
                    speed={speechSpeed}
                    onSpeedChange={setSpeechSpeed}
                    compact={true}
                    theme="dark"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Progress */}
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white font-medium">Verses Memorized</span>
                  <span className="text-emerald-400 font-bold">{child?.scriptures_memorized || 0}/50</span>
                </div>
                <Progress value={Math.min(((child?.scriptures_memorized || 0) / 50) * 100, 100)} className="h-3" />
                <p className="text-xs text-slate-400 mt-2">
                  {50 - (child?.scriptures_memorized || 0)} more verses to reach the graduation milestone!
                </p>
              </CardContent>
            </Card>

            {/* Memorized Scriptures List */}
            {dashboard?.scriptures?.length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700/50">
                <CardHeader>
                  <CardTitle className="text-base text-white">Memorized Scriptures</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {dashboard.scriptures.map((scripture, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <div>
                        <p className="text-emerald-300 text-sm font-medium">{scripture.reference}</p>
                        <p className="text-slate-400 text-xs">{scripture.text?.substring(0, 60)}...</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {(!dashboard?.scriptures || dashboard.scriptures.length === 0) && (
              <Card className="bg-slate-800/30 border-slate-700/30">
                <CardContent className="p-8 text-center">
                  <BookOpen className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                  <p className="text-slate-400">No verses memorized yet</p>
                  <p className="text-slate-500 text-sm mt-1">Start learning this week's verse!</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="space-y-4" data-testid="leaderboard-tab">
            <div className="text-center mb-2">
              <h2 className="text-xl font-bold text-white flex items-center justify-center gap-2">
                <Medal className="w-6 h-6 text-amber-400" /> Worksheet Leaderboard
              </h2>
              <p className="text-slate-400 text-xs mt-1">See how you compare with other learners!</p>
            </div>

            {loadingLeaderboard ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
              </div>
            ) : leaderboard?.leaderboard?.length > 0 ? (
              <>
                {/* Top 3 Podium */}
                <div className="flex items-end justify-center gap-3 mb-4 px-2">
                  {[1, 0, 2].map(idx => {
                    const entry = leaderboard.leaderboard[idx];
                    if (!entry) return <div key={idx} className="w-1/3" />;
                    const isMe = entry.child_id === childId;
                    const heights = { 0: 'h-28', 1: 'h-24', 2: 'h-20' };
                    const medals = { 0: { bg: 'from-amber-500 to-yellow-600', icon: '1st', ring: 'ring-amber-400' }, 1: { bg: 'from-slate-300 to-slate-400', icon: '2nd', ring: 'ring-slate-300' }, 2: { bg: 'from-orange-600 to-orange-700', icon: '3rd', ring: 'ring-orange-500' } };
                    const m = medals[idx];
                    return (
                      <motion.div key={idx} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: idx * 0.15 }}
                        className={`w-1/3 flex flex-col items-center ${isMe ? 'scale-105' : ''}`}>
                        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${m.bg} flex items-center justify-center text-white font-bold text-sm mb-1 ${isMe ? `ring-2 ${m.ring}` : ''}`}>
                          {m.icon}
                        </div>
                        <p className={`text-xs font-bold truncate w-full text-center ${isMe ? 'text-amber-300' : 'text-white'}`}>
                          {entry.name}{isMe ? ' (You)' : ''}
                        </p>
                        <div className={`w-full ${heights[idx]} bg-gradient-to-t from-indigo-600/40 to-indigo-500/20 rounded-t-xl border border-indigo-500/30 flex flex-col items-center justify-center mt-1 ${isMe ? 'border-amber-400/50' : ''}`}>
                          <p className="text-amber-400 font-bold text-lg">{entry.total_xp}</p>
                          <p className="text-[10px] text-slate-400">XP</p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Full Ranking List */}
                <Card className="bg-slate-800/40 border-slate-700/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                      <Users className="w-4 h-4" /> All Rankings
                      <span className="ml-auto text-xs text-slate-500">{leaderboard.total_participants} learners</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1.5 max-h-80 overflow-y-auto">
                    {leaderboard.leaderboard.map((entry) => {
                      const isMe = entry.child_id === childId;
                      const levelColors = { seeds: 'text-emerald-400', roots: 'text-blue-400', warriors: 'text-purple-400' };
                      return (
                        <motion.div key={entry.child_id}
                          initial={{ x: -10, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: entry.rank * 0.03 }}
                          className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${isMe ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-slate-700/20 hover:bg-slate-700/40'}`}
                          data-testid={`leaderboard-entry-${entry.rank}`}>
                          {/* Rank */}
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                            entry.rank === 1 ? 'bg-amber-500 text-white' :
                            entry.rank === 2 ? 'bg-slate-400 text-white' :
                            entry.rank === 3 ? 'bg-orange-600 text-white' :
                            'bg-slate-700 text-slate-400'
                          }`}>
                            {entry.rank}
                          </div>

                          {/* Name & Level */}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isMe ? 'text-amber-300' : 'text-white'}`}>
                              {entry.name} {isMe && <span className="text-[10px] text-amber-400/80">(You)</span>}
                            </p>
                            <p className={`text-[10px] capitalize ${levelColors[entry.level] || 'text-slate-500'}`}>
                              {entry.level} Level
                            </p>
                          </div>

                          {/* Stats */}
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div className="text-center">
                              <p className="text-xs font-bold text-amber-400">{entry.total_xp}</p>
                              <p className="text-[9px] text-slate-500">XP</p>
                            </div>
                            <div className="text-center">
                              <p className={`text-xs font-bold ${entry.avg_score >= 80 ? 'text-emerald-400' : entry.avg_score >= 60 ? 'text-amber-400' : 'text-slate-400'}`}>
                                {entry.avg_score}%
                              </p>
                              <p className="text-[9px] text-slate-500">Avg</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs font-bold text-indigo-400">{entry.worksheets_completed}</p>
                              <p className="text-[9px] text-slate-500">Done</p>
                            </div>
                            {entry.perfect_scores > 0 && (
                              <div className="flex items-center gap-0.5">
                                <Star className="w-3 h-3 text-amber-400" />
                                <span className="text-[10px] text-amber-400 font-bold">{entry.perfect_scores}</span>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="bg-slate-800/40 border-slate-700/50">
                <CardContent className="p-10 text-center">
                  <Medal className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 font-medium">No rankings yet</p>
                  <p className="text-slate-500 text-xs mt-1">Complete worksheets to appear on the leaderboard!</p>
                  <Button onClick={() => navigate(`/christian/lesson/${childId}/${dashboard?.current_lesson?.week || 1}`)}
                    className="mt-4 bg-gradient-to-r from-indigo-500 to-purple-600" size="sm">
                    Start a Lesson <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Word Puzzles Tab */}
        {activeTab === 'puzzles' && (
          <div className="space-y-4" data-testid="puzzles-tab">
            {/* Back to puzzle selection */}
            {puzzle && !puzzleResult ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <button onClick={() => { setPuzzle(null); setPuzzleType(null); }} className="text-purple-400 text-sm flex items-center gap-1">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <span className="text-xs text-slate-500 capitalize">{puzzle.subject} · {puzzle.difficulty}</span>
                </div>
                {/* Inline puzzle game */}
                {puzzleType === 'scramble' && puzzle.data?.map((q, i) => (
                  <Card key={q.id} className="bg-slate-800/40 border-slate-700/50 mb-2">
                    <CardContent className="p-3 flex items-center gap-3">
                      <span className="text-lg font-black tracking-[0.2em] text-fuchsia-400 font-mono">{q.scrambled}</span>
                      <Input placeholder={`${q.length} letters`} className="flex-1 bg-slate-700/50 border-slate-600 text-white"
                        data-testid={`christian-scramble-${i}`}
                        onChange={e => {
                          const val = e.target.value;
                          setPuzzle(prev => {
                            const newData = [...prev.data];
                            newData[i] = { ...newData[i], userAnswer: val };
                            return { ...prev, data: newData };
                          });
                        }} />
                      <span className="text-xs text-slate-500">{q.points}pts</span>
                    </CardContent>
                  </Card>
                ))}
                {puzzleType === 'missing' && puzzle.data?.map((q, i) => (
                  <Card key={q.id} className="bg-slate-800/40 border-slate-700/50 mb-2">
                    <CardContent className="p-3 flex items-center gap-3">
                      <span className="text-lg font-black tracking-[0.3em] text-emerald-400 font-mono">{q.display}</span>
                      <Input placeholder="Full word" className="flex-1 bg-slate-700/50 border-slate-600 text-white"
                        data-testid={`christian-missing-${i}`}
                        onChange={e => {
                          const val = e.target.value;
                          setPuzzle(prev => {
                            const newData = [...prev.data];
                            newData[i] = { ...newData[i], userAnswer: val };
                            return { ...prev, data: newData };
                          });
                        }} />
                    </CardContent>
                  </Card>
                ))}
                {puzzleType === 'crossword' && puzzle.data?.map((q, i) => (
                  <Card key={q.id} className="bg-slate-800/40 border-slate-700/50 mb-2">
                    <CardContent className="p-3">
                      <p className="text-sm text-slate-300 mb-2">
                        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 mr-1">{q.number}. {q.direction?.toUpperCase()}</span>
                        {q.clue}
                      </p>
                      <Input placeholder={`${q.length} letters`} className="font-mono tracking-widest uppercase bg-slate-700/50 border-slate-600 text-white"
                        data-testid={`christian-crossword-${i}`}
                        onChange={e => {
                          const val = e.target.value;
                          setPuzzle(prev => {
                            const newData = [...prev.data];
                            newData[i] = { ...newData[i], userAnswer: val };
                            return { ...prev, data: newData };
                          });
                        }} />
                    </CardContent>
                  </Card>
                ))}
                {puzzleType === 'search' && puzzle.data && (
                  <Card className="bg-slate-800/40 border-slate-700/50">
                    <CardContent className="p-3 text-center">
                      <div className="flex flex-wrap gap-1.5 mb-3 justify-center">
                        {puzzle.data.words?.map(w => (
                          <span key={w} className="px-2 py-1 rounded-full text-xs font-bold bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">{w}</span>
                        ))}
                      </div>
                      <div className="inline-grid gap-0.5 bg-slate-700/50 p-2 rounded-xl" style={{ gridTemplateColumns: `repeat(${puzzle.data.size}, 1fr)` }}>
                        {puzzle.data.grid?.map((row, r) => row.map((ch, c) => (
                          <button key={`${r}-${c}`} className="w-7 h-7 flex items-center justify-center text-xs font-bold rounded bg-slate-800 text-slate-300 hover:bg-cyan-500 hover:text-white transition-all">
                            {ch}
                          </button>
                        )))}
                      </div>
                      <p className="text-xs text-slate-500 mt-2">Find the words in the grid!</p>
                    </CardContent>
                  </Card>
                )}
                {/* Submit button for non-search types */}
                {puzzleType !== 'search' && (
                  <Button className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white mt-3"
                    data-testid="christian-puzzle-submit"
                    onClick={() => {
                      const items = puzzle.data.map(q => ({
                        id: q.id, answer: q.answer, points: q.points,
                        user_answer: (q.userAnswer || '').trim(),
                      }));
                      submitPuzzle({ items });
                    }}>
                    <Check className="w-4 h-4 mr-2" /> Check Answers
                  </Button>
                )}
              </div>
            ) : puzzleResult ? (
              /* Results view */
              <Card className="bg-amber-900/20 border-amber-500/30">
                <CardContent className="p-6 text-center">
                  <Trophy className={`w-12 h-12 mx-auto mb-2 ${puzzleResult.score >= 70 ? 'text-amber-400' : 'text-slate-400'}`} />
                  <p className="text-3xl font-black text-white">{puzzleResult.score}%</p>
                  <p className="text-amber-400 text-sm">+{puzzleResult.xp_earned} XP earned!</p>
                  <p className="text-xs text-slate-500 mt-1">{puzzleResult.message}</p>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={() => { setPuzzleResult(null); generatePuzzle(puzzleType); }}
                      className="flex-1 bg-gradient-to-r from-fuchsia-500 to-pink-500" size="sm" data-testid="puzzle-play-again">
                      Play Again
                    </Button>
                    <Button onClick={() => { setPuzzle(null); setPuzzleType(null); setPuzzleResult(null); }}
                      variant="outline" className="flex-1 border-slate-600 text-slate-300" size="sm" data-testid="puzzle-change">
                      Different Puzzle
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Puzzle selection */
              <>
                <div className="text-center mb-3">
                  <h2 className="text-lg font-bold text-white flex items-center justify-center gap-2">
                    <Puzzle className="w-5 h-5 text-fuchsia-400" /> Word Puzzles
                  </h2>
                  <p className="text-slate-400 text-xs">Choose a puzzle type!</p>
                </div>
                {/* Subject selector */}
                <div className="flex flex-wrap justify-center gap-1.5 mb-3">
                  {[
                    { key: 'bible', label: 'Bible', color: 'amber' },
                    { key: 'general', label: 'General', color: 'emerald' },
                    { key: 'reading', label: 'Reading', color: 'sky' },
                  ].map(s => (
                    <button key={s.key} onClick={() => setPuzzleSubject(s.key)}
                      data-testid={`christian-subject-${s.key}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                        puzzleSubject === s.key
                          ? `bg-${s.color}-500/30 text-${s.color}-400 border border-${s.color}-500/50`
                          : 'bg-slate-800 text-slate-400 border border-slate-700'
                      }`}>
                      {s.label}
                    </button>
                  ))}
                </div>
                {/* Puzzle type grid */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'scramble', label: 'Scramble', desc: 'Unscramble letters', color: 'from-fuchsia-500 to-pink-500' },
                    { key: 'search', label: 'Word Search', desc: 'Find hidden words', color: 'from-cyan-500 to-blue-500' },
                    { key: 'crossword', label: 'Crossword', desc: 'Solve the clues', color: 'from-amber-500 to-orange-500' },
                    { key: 'missing', label: 'Missing Letters', desc: 'Fill the blanks', color: 'from-emerald-500 to-green-500' },
                  ].map(pt => (
                    <motion.button key={pt.key} whileTap={{ scale: 0.95 }}
                      onClick={() => generatePuzzle(pt.key)}
                      data-testid={`christian-puzzle-${pt.key}`}
                      disabled={generatingPuzzle}
                      className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-4 text-center hover:border-purple-500/50 transition-all">
                      <div className={`w-10 h-10 mx-auto rounded-xl bg-gradient-to-br ${pt.color} flex items-center justify-center mb-2`}>
                        {generatingPuzzle && puzzleType === pt.key ? (
                          <Loader2 className="w-5 h-5 text-white animate-spin" />
                        ) : (
                          <Puzzle className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <p className="text-white text-sm font-bold">{pt.label}</p>
                      <p className="text-slate-500 text-[10px]">{pt.desc}</p>
                    </motion.button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-md border-t border-white/10 px-4 py-2">
        <div className="flex justify-around">
          {[
            { id: 'home', icon: Cross, label: 'Home' },
            { id: 'lessons', icon: BookOpen, label: 'Lessons', action: () => navigate(`/christian/lesson/${childId}/${dashboard?.current_lesson?.week || 1}`) },
            { id: 'puzzles', icon: Puzzle, label: 'Puzzles' },
            { id: 'leaderboard', icon: Medal, label: 'Ranks' },
            { id: 'memory', icon: Heart, label: 'Memory' },
            { id: 'badges', icon: Trophy, label: 'Badges' }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => item.action ? item.action() : setActiveTab(item.id)}
              className={`flex flex-col items-center py-2 px-4 rounded-xl transition-colors ${
                activeTab === item.id
                  ? 'text-purple-400 bg-purple-500/20'
                  : 'text-slate-400'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs mt-1">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Quiz Modal */}
      <AnimatePresence>
        {showQuiz && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl w-full max-w-lg max-h-[90vh] overflow-auto border border-purple-500/30"
            >
              {/* Quiz Header */}
              <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-white">Scripture Quiz</p>
                    <p className="text-xs text-slate-400">
                      {quizResults ? 'Results' : `Question ${currentQuestionIndex + 1} of ${quizData?.questions?.length || 0}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowQuiz(false);
                    setQuizResults(null);
                    setQuizData(null);
                  }}
                  className="p-2 rounded-lg hover:bg-slate-700 transition-colors"
                  data-testid="close-quiz-btn"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              {/* Quiz Content */}
              <div className="p-6">
                {quizResults ? (
                  /* Results Screen */
                  <div className="text-center space-y-6">
                    <div className={`text-6xl ${quizResults.perfect_score ? 'animate-bounce' : ''}`}>
                      {quizResults.perfect_score ? '🌟' : quizResults.correct_count > 0 ? '👏' : '💪'}
                    </div>
                    
                    <div>
                      <p className="text-2xl font-bold text-white mb-2">
                        {quizResults.perfect_score ? 'Perfect Score!' : 
                         quizResults.correct_count > quizResults.total_questions / 2 ? 'Great Job!' : 'Keep Practicing!'}
                      </p>
                      <p className="text-slate-400">
                        You got {quizResults.correct_count} out of {quizResults.total_questions} correct
                      </p>
                    </div>

                    <Card className="bg-gradient-to-br from-emerald-900/30 to-teal-900/30 border-emerald-500/30">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-center gap-2">
                          <Star className="w-6 h-6 text-amber-400" />
                          <span className="text-2xl font-bold text-amber-400">+{quizResults.xp_earned} XP</span>
                        </div>
                        <p className="text-sm text-slate-400 mt-1">
                          New Total: {quizResults.new_total_xp} XP
                        </p>
                      </CardContent>
                    </Card>

                    {quizResults.new_badge && (
                      <Card className="bg-gradient-to-br from-amber-900/30 to-orange-900/30 border-amber-500/30">
                        <CardContent className="p-4">
                          <p className="text-amber-400 font-bold mb-2">New Badge Earned!</p>
                          <div className="flex items-center justify-center gap-3">
                            <span className="text-4xl">🏆</span>
                            <div className="text-left">
                              <p className="text-white font-medium">{quizResults.new_badge.badge_name}</p>
                              <p className="text-xs text-slate-400">{quizResults.new_badge.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Answer Review */}
                    <div className="space-y-2 text-left">
                      <p className="text-sm font-medium text-slate-400">Answer Review:</p>
                      {quizResults.results.map((result, idx) => (
                        <div key={idx} className={`flex items-center gap-2 p-2 rounded-lg ${
                          result.is_correct ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'
                        }`}>
                          {result.is_correct ? (
                            <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                          ) : (
                            <X className="w-4 h-4 text-red-400 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm truncate ${result.is_correct ? 'text-emerald-300' : 'text-red-300'}`}>
                              {result.user_answer || '(no answer)'}
                            </p>
                            {!result.is_correct && (
                              <p className="text-xs text-slate-500">Correct: {result.correct_answer}</p>
                            )}
                          </div>
                          <span className="text-xs text-slate-400">+{result.xp_earned} XP</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-3">
                      <Button
                        onClick={() => {
                          setQuizResults(null);
                          startQuiz();
                        }}
                        className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500"
                        data-testid="try-again-btn"
                      >
                        Try Again
                      </Button>
                      <Button
                        onClick={() => {
                          setShowQuiz(false);
                          setQuizResults(null);
                          fetchDashboard(); // Refresh data
                        }}
                        variant="outline"
                        className="flex-1 border-slate-600 text-white"
                        data-testid="done-quiz-btn"
                      >
                        Done
                      </Button>
                    </div>
                  </div>
                ) : currentQuestion ? (
                  /* Question Screen */
                  <div className="space-y-6">
                    {/* Progress Dots */}
                    <div className="flex justify-center gap-2">
                      {quizData?.questions?.map((_, idx) => (
                        <div
                          key={idx}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            idx === currentQuestionIndex
                              ? 'bg-purple-500 w-4'
                              : quizAnswers[quizData.questions[idx].id]
                              ? 'bg-emerald-500'
                              : 'bg-slate-600'
                          }`}
                        />
                      ))}
                    </div>

                    {/* XP Badge */}
                    <div className="flex justify-center">
                      <span className="bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-sm">
                        <Star className="w-3 h-3 inline mr-1" />
                        {currentQuestion.xp_reward} XP
                      </span>
                    </div>

                    {/* Question */}
                    <div className="text-center">
                      <p className="text-white text-lg font-medium mb-2">
                        {currentQuestion.question}
                      </p>
                      {currentQuestion.verse && (
                        <p className="text-purple-300 text-base italic bg-purple-500/10 p-3 rounded-lg">
                          "{currentQuestion.verse}"
                        </p>
                      )}
                    </div>

                    {/* Answer Input */}
                    {currentQuestion.type === 'fill_blank' ? (
                      <div className="space-y-2">
                        <Input
                          value={quizAnswers[currentQuestion.id] || ''}
                          onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                          placeholder="Type your answer..."
                          className="bg-slate-700 border-slate-600 text-white text-center text-lg"
                          autoFocus
                          data-testid="fill-blank-input"
                        />
                        <button
                          onClick={() => toast.info(currentQuestion.hint)}
                          className="text-xs text-slate-500 hover:text-purple-400 flex items-center gap-1 mx-auto"
                        >
                          <HelpCircle className="w-3 h-3" />
                          Need a hint?
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {currentQuestion.options?.map((option, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleAnswer(currentQuestion.id, option)}
                            className={`w-full p-4 rounded-xl text-left transition-all ${
                              quizAnswers[currentQuestion.id] === option
                                ? 'bg-purple-500/30 border-2 border-purple-500 text-white'
                                : 'bg-slate-700/50 border-2 border-slate-600 text-slate-300 hover:border-slate-500'
                            }`}
                            data-testid={`option-${idx}`}
                          >
                            <span className="font-medium">{String.fromCharCode(65 + idx)}.</span> {option}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Navigation */}
                    <div className="flex gap-3">
                      {currentQuestionIndex > 0 && (
                        <Button
                          onClick={() => setCurrentQuestionIndex(prev => prev - 1)}
                          variant="outline"
                          className="border-slate-600 text-white"
                        >
                          Previous
                        </Button>
                      )}
                      
                      {!isLastQuestion ? (
                        <Button
                          onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                          disabled={!quizAnswers[currentQuestion.id]}
                          className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500"
                          data-testid="next-question-btn"
                        >
                          Next
                        </Button>
                      ) : (
                        <Button
                          onClick={submitQuiz}
                          disabled={!allAnswered || quizLoading}
                          className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500"
                          data-testid="submit-quiz-btn"
                        >
                          {quizLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              Submit Quiz
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-500 mx-auto" />
                    <p className="text-slate-400 mt-2">Loading quiz...</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChristianChildDashboard;
